import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';

/**
 * Serviço para gerenciamento de solicitações
 */
@Injectable()
export class SolicitacaoService {
  private urlBase = `${environment.urlAPI}/solicitacao`;

  constructor(private http: HttpClient) { }

  /**
 * Recupera a lista de solicitações
 * @param codUsuario Código do usuário
 * @param fornecedor Flag indicando se é fornecedor (opcional)
 * @returns Promise com a lista de solicitações
 */
public getListaSolicitacao(codUsuario: number, fornecedor?: boolean): Promise<any> {
  return new Promise((resolve, reject) => {
    let params = new HttpParams().set('codUsuario', codUsuario.toString());
    
    if (fornecedor !== null && fornecedor !== undefined) {
      params = params.append('fornecedor', fornecedor.toString());
    }
    
    this.http.get(`${this.urlBase}-lista`, { params })
      .subscribe({
        next: (response: any) => {
          if (response.type === true) {
            resolve(response.data.retorno);
          } else {
            reject(response.message || 'Erro ao recuperar lista de solicitações');
          }
        },
        error: (error) => {
          reject(error);
        }
      });
  });
}

  /**
   * Atualiza o status de uma solicitação
   * @param solicitacao Dados da solicitação
   * @returns Promise com o resultado da operação
   */
  public updateSolicitacaoStatus(solicitacao: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlBase}-atualizastatus`, solicitacao)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.message || 'Erro ao atualizar status da solicitação');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Cria um workflow de solicitação para o tipo desejado
   * @param solicitacao Dados da solicitação
   * @returns Promise com o resultado da operação
   */
  public gravaSolicitacao(solicitacao: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlBase}-salvarsolicitacao`, solicitacao)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.message || 'Erro ao gravar solicitação');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Cria uma solicitação sem workflow
   * @param solicitacao Dados da solicitação
   * @returns Promise com o resultado da operação
   */
  public gravaSolicitacaoSemWorkflow(solicitacao: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlBase}-salvarsolicitacao-semworkflow`, solicitacao)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.message || 'Erro ao gravar solicitação sem workflow');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Grava uma solicitação de desistência
   * @param solicitacao Dados da solicitação de desistência
   * @returns Promise com o resultado da operação
   */
  public gravaSolicitacaoDesistencia(solicitacao: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlBase}-salvardesistencia`, solicitacao)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.message || 'Erro ao gravar solicitação de desistência');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Grava a solicitação de alteração de cadastro
   * @param codSolicitacao Código da solicitação
   * @param campos Campos a serem alterados
   * @returns Promise com o resultado da operação
   */
  public gravaSolicitacaoAlteracaoCadastro(codSolicitacao: number, campos: string): Promise<any> {
    const camposPayload = typeof campos === 'string' ? campos : '';
    return new Promise((resolve, reject) => {
      const payload = {
        codSolicitacao,
        campos: camposPayload
      };
      
      this.http.post(`${this.urlBase}-solicitacaoaltcadastro`, payload)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.data || response);
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera uma solicitação de alteração de cadastro
   * @param codSolicitacao Código da solicitação
   * @returns Promise com os dados da solicitação
   */
  public getSolicitacaoAlteracaoCadastro(codSolicitacao: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const params = new HttpParams().set('codSolicitacao', codSolicitacao.toString());
      
      this.http.get(`${this.urlBase}-getaltcadastro`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.retorno);
            } else {
              reject(response.message || 'Erro ao recuperar solicitação de alteração de cadastro');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Verifica se existe solicitação de alteração de cadastro
   * @returns Promise com o resultado da verificação
   */
  public verificaSolicitacaoAlteracaoCadastro(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlBase}-verificaaltcadastro`)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.retorno);
            } else {
              reject(response.message || 'Erro ao verificar solicitação de alteração de cadastro');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Realiza a alteração dos campos de cadastro
   * @param codSolicitacao Código da solicitação
   * @param codUsuario Código do usuário
   * @returns Promise com o resultado da operação
   */
  public gravaAlteracaoCadastro(codSolicitacao: number, codUsuario: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const payload = {
        codSolicitacao,
        codUsuario
      };
      
      this.http.post(`${this.urlBase}-alteracaocadastro`, payload)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              resolve(response); // Mantendo o comportamento original que resolve mesmo com erro
            }
          },
          error: (error) => {
            resolve(error); // Mantendo o comportamento original que resolve mesmo com erro
          }
        });
    });
  }

  /**
   * Grava solicitação de desenvolvimento
   * @param solicitacao Dados da solicitação de desenvolvimento
   * @returns Promise com o resultado da operação
   */
  public gravaDesenvolvimento(solicitacao: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlBase}-desenvolvimento`, solicitacao)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.message || 'Erro ao gravar solicitação de desenvolvimento');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera lista de sistemas de gestão para desistência
   * @returns Promise com a lista de sistemas de gestão
   */
  public getSistemaGestaoDesistencia(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlBase}-solicitacaodesistencia`)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.retorno);
            } else {
              reject(response.message || 'Erro ao recuperar sistemas de gestão para desistência');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera lista de sistemas de gestão para desenvolvimento
   * @returns Promise com a lista de sistemas de gestão
   */
  public getSistemaGestaoDesenvolvimento(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.urlBase}-solicitacaodesenvolvimento`)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.retorno);
            } else {
              reject(response.message || 'Erro ao recuperar sistemas de gestão para desenvolvimento');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera lista das auditorias que podem gerar uma apelação
   * @param codUsuario Código do usuário
   * @returns Promise com a lista de auditorias
   */
  public getAuditoriasApelacao(codUsuario: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const params = new HttpParams().set('codUsuario', codUsuario.toString());
      
      this.http.get(`${this.urlBase}-getapelacao`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.retorno);
            } else {
              reject(response.message || 'Erro ao recuperar auditorias para apelação');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera lista de certificados para cancelamento
   * @param codUsuario Código do usuário
   * @returns Promise com a lista de certificados
   */
  public getCertificadosCancelamento(codUsuario: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const params = new HttpParams().set('codUsuario', codUsuario.toString());
      
      this.http.get(`${this.urlBase}-getcancelamento`, { params })
        .subscribe({
          
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.retorno);
            } else {
              reject(response.message || 'Erro ao recuperar certificados para cancelamento');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Altera o status de uma solicitação pelo código
   * @param codSolicitacao Código da solicitação
   * @param statusSolicitacao Novo status
   * @returns Promise com o resultado da operação
   */
  public alterarStatusSolicitacaoByCodSolicitacao(codSolicitacao: number, statusSolicitacao: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const payload = {
        codSolicitacao,
        statusSolicitacao
      };
      
      this.http.post(`${this.urlBase}-alterarstatus-byuid`, payload)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.message || 'Erro ao alterar status da solicitação');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Grava desistência
   * @param solicitacao Dados da desistência
   * @returns Promise com o resultado da operação
   */
  public gravaDesistencia(solicitacao: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlBase}-desistencia`, solicitacao)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.message || 'Erro ao gravar desistência');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Grava cancelamento de certificado
   * @param codCertificado Código do certificado
   * @param codMotivoCancelamento Código do motivo de cancelamento
   * @returns Promise com o resultado da operação
   */
  public gravaCancelamento(payload: object): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.urlBase}-cancelamento`, payload)
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.message || 'Erro ao gravar cancelamento');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera uma solicitação pelo código
   * @param codSolicitacao Código da solicitação
   * @returns Promise com os dados da solicitação
   */
  public getSolicitacaoByCodigo(codSolicitacao: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const params = new HttpParams().set('codSolicitacao', codSolicitacao.toString());
      
      this.http.get(`${this.urlBase}-getSolicitacaoByCodigo`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.solicitacao);
            } else {
              reject(response.message || 'Erro ao recuperar solicitação');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera dados de uma solicitação pelo código
   * @param codSolicitacao Código da solicitação
   * @returns Promise com os dados detalhados da solicitação
   */
  public getDadosSolicitacaoByCodigo(codSolicitacao: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const params = new HttpParams().set('codSolicitacao', codSolicitacao.toString());
      
      this.http.get(`${this.urlBase}-getDadosSolicitacaoByCodigo`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data);
            } else {
              reject(response.message || 'Erro ao recuperar dados da solicitação');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Recupera uma solicitação pelo código do workflow
   * @param codWorkflow Código do workflow
   * @returns Promise com os dados da solicitação
   */
  public getSolicitacaoByCodWorkflow(codWorkflow: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const params = new HttpParams().set('codWorkflow', codWorkflow.toString());
      
      this.http.get(`${this.urlBase}-getSolicitacaoByCodWorkflow`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.solicitacao);
            } else {
              reject(response.message || 'Erro ao recuperar solicitação por código de workflow');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }

  /**
   * Verifica escopo de certificado
   * @param codCertificado Código do certificado
   * @returns Promise com o resultado da verificação
   */
  public getVerificacaoEscopo(codCertificado: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const params = new HttpParams().set('codCertificado', codCertificado.toString());
      
      this.http.get(`${this.urlBase}-verificacaoEscopo`, { params })
        .subscribe({
          next: (response: any) => {
            if (response.type === true) {
              resolve(response.data.retorno);
            } else {
              reject(response.message || 'Erro ao verificar escopo do certificado');
            }
          },
          error: (error) => {
            reject(error);
          }
        });
    });
  }
}


// WEBPACK FOOTER //
// /app/src/app/services/solicitacao.service.ts